import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Box } from '#/components/Box/Box';
import { ButtonBar } from '#/components/ButtonBar/ButtonBar';
import { Divider } from '#/components/Divider/Divider';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { Hide } from '#/components/Hide/Hide';
import { Icon } from '#/components/Icon/Icon';
import { OrgStatBadge } from '#/components/OrgStatBadge/OrgStatBadge';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { BodySmall } from '#/components/Text/BodySmall';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { useSaveListing } from '#/modules/listing/hooks/useSaveListing';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import { useOrgSavedToast } from '#/modules/listing/orgs/hooks/useOrgSavedToast';
import { orgDashboardApiPartialUpdateOrg } from '#/modules/orgDashboard/api/orgDashboardApiOrg';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getSocialNetworkFromLink } from '#/utils/social/getSocialNetworkFromLink';
import { iconNameBySocialNetwork } from '#/utils/social/iconNameBySocialNetwork';
import { showModal } from '#/zustand-stores/modalStore';

import {
  ViewOrgPageFooterHolder,
  ViewOrgPageFooterWrapper,
  ViewOrgPageLayoutHeader,
  ViewOrgPageLayoutSidebarTitle,
  ViewOrgPageSidebarLink,
  ViewOrgPageSidebarLinkCandid,
  ViewOrgPageSidebarLinkLi,
  ViewOrgPageSidebarLinkUl,
} from './ViewOrgPage.styled';
import { ViewOrgPageActionButtons } from './ViewOrgPageActionButtons';
import { ViewOrgPageBanner } from './ViewOrgPageBanner';
import { ViewOrgPageDetails } from './ViewOrgPageDetails';
import { ViewOrgPageHeader } from './ViewOrgPageHeader';
import { ViewOrgPageSidebarDetails } from './ViewOrgPageSidebarDetails';
import { ViewOrgPageSidebarHeader } from './ViewOrgPageSidebarHeader';
import { ViewOrgPageSidebarImage } from './ViewOrgPageSidebarImage';
import { ViewOrgPageSidebarTitle } from './ViewOrgPageSidebarTitle';
import { ViewOrgToolbar } from './components/ViewOrgToolbar';

type Props = {
  initialOrg: ApiOrg;
  initiallySaved: boolean;
  onUpdateOrg?: (org: OrgDashboardApiOrg) => void;
  isPreview: boolean;
  isPublished: boolean;
};

export function ViewOrgPage({
  initialOrg,
  initiallySaved,
  onUpdateOrg,
  isPreview,
  isPublished,
}: Props) {
  const { i18n, locale } = useI18n();

  const [org, setOrg] = useState<ApiOrg>(initialOrg);

  const location = useLocation();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(true);
  const { user } = useUser();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: org.id,
  });

  const orgOwner = useMemo(
    () => Boolean(user?.orgs?.some((o) => o.id === org.id)),
    [user, org],
  );

  useTrackPageView('Org Listing Page', {
    name: org.name,
    id: org.id,
    org_type: org.orgType,
    page_type: 'Org Listings',
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (
      queryParams.has('admin-request') &&
      user?.emailVerified &&
      user?.firstName
    ) {
      showModal('ADMIN_REQUEST', {
        objectId: org.id,
        objectName: org.name,
      });
      queryParams.delete('admin-request');

      const newUrl = `${location.pathname}${queryParams}`;
      void navigate(newUrl);
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    org.id,
    org.name,
    user?.emailVerified,
    user?.firstName,
  ]);

  const onSubmit: FormSubmitFunction<
    Pick<ApiOrg, 'id'> & Partial<ApiOrg>
  > = async ({ values, handleError }) => {
    try {
      const apiOrgResponse = await orgDashboardApiPartialUpdateOrg(values);
      setOrg(apiOrgResponse);
      onUpdateOrg?.(apiOrgResponse);
    } catch (error) {
      handleError(error as Error);
    }
  };

  const socialLinks = useMemo(
    () =>
      org.links
        .map((link) => {
          const socialNetwork = getSocialNetworkFromLink(link);
          if (!socialNetwork) return;

          return { link, socialNetwork };
        })
        .filter(Boolean),
    [org.links],
  );

  const orgStat = useMemo(() => {
    let fragment;
    if (org.hiring) {
      fragment = (
        <OrgStatBadge iconName="star-check" text={i18n('Actively Hiring')} />
      );
    } else if (org.hasUpcomingEvents) {
      fragment = (
        <OrgStatBadge iconName="calendar" text={i18n('Upcoming Events')} />
      );
    } else if (org.seekingVolunteers) {
      fragment = (
        <OrgStatBadge iconName="group" text={i18n('Seeking Volunteers')} />
      );
    }
    if (fragment) {
      return <div style={{ marginBottom: 24 }}>{fragment}</div>;
    }
    return null;
  }, [i18n, org.hasUpcomingEvents, org.hiring, org.seekingVolunteers]);

  const { showOrgSavedToast } = useOrgSavedToast();

  const { save, saved, unsave } = useSaveListing({
    listingId: org.id,
    listingType: 'ORG',
    initiallySaved,
    orgId: org.id,
    orgType: org.orgType,
    onSaved: showOrgSavedToast,
    onUnsaved: null,
    trackClickEvent: true,
    searchSavedListingsStore: null,
  });

  return (
    <>
      {orgOwner && (
        <ViewOrgToolbar
          org={org as OrgDashboardApiOrg}
          isPublished={isPublished}
          editMode={editMode}
          setEditMode={setEditMode}
        />
      )}

      {((orgOwner && editMode) || org.banner) && (
        <ViewOrgPageBanner
          org={org}
          isPublished={isPublished}
          isPreview={isPreview}
          orgOwner={orgOwner}
          editMode={editMode}
          onSubmit={onSubmit}
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar qaId="org-page-sidebar" maxWidth={288}>
          <BodySmall lineHeight="normal">
            <Hide xs sm md>
              <Box mb="16px">
                <ViewOrgPageSidebarImage
                  org={org}
                  orgOwner={orgOwner}
                  editMode={editMode}
                  onSubmit={onSubmit}
                />
              </Box>
            </Hide>

            {orgStat}

            <ViewOrgPageLayoutSidebarTitle aria-hidden="true">
              <Hide xs sm md>
                <ViewOrgPageSidebarTitle org={org} />
              </Hide>
            </ViewOrgPageLayoutSidebarTitle>

            <ViewOrgPageSidebarLinkUl>
              {socialLinks.map(({ socialNetwork, link }) => (
                <ViewOrgPageSidebarLinkLi key={link}>
                  {socialNetwork === 'candid' ? (
                    <ViewOrgPageSidebarLinkCandid
                      href={link}
                      title={
                        socialNetwork.at(0)?.toUpperCase() +
                        socialNetwork.slice(1)
                      }
                    >
                      <Icon
                        name={iconNameBySocialNetwork[socialNetwork]}
                        size={16}
                      />
                    </ViewOrgPageSidebarLinkCandid>
                  ) : (
                    <ViewOrgPageSidebarLink
                      href={link}
                      title={
                        socialNetwork.at(0)?.toUpperCase() +
                        socialNetwork.slice(1)
                      }
                    >
                      <Icon
                        name={iconNameBySocialNetwork[socialNetwork]}
                        size={16}
                      />
                    </ViewOrgPageSidebarLink>
                  )}
                </ViewOrgPageSidebarLinkLi>
              ))}
            </ViewOrgPageSidebarLinkUl>

            <Divider marginTop={24} marginBottom={24} />

            <ViewOrgPageSidebarHeader org={org} />

            <ViewOrgPageSidebarDetails org={org} orgOwner={orgOwner} />
          </BodySmall>
        </ListingViewSidebar>

        <ListingViewMain>
          <ViewOrgPageLayoutHeader>
            {searchContext && (
              <PrintHidden>
                <Box mb={[4, 4, 4, '40px']}>
                  <SearchContextHeader searchContext={searchContext} />
                </Box>
              </PrintHidden>
            )}

            <ViewOrgPageHeader
              org={org}
              isPreview={isPreview}
              saved={saved}
              onSave={save}
              onUnsave={unsave}
            />
          </ViewOrgPageLayoutHeader>

          <ViewOrgPageDetails org={org} />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!isPreview && !user && (
        <PrintHidden>
          <ViewOrgPageFooterHolder>
            <ViewOrgPageFooterWrapper>
              <ListingViewSignupCta listingUrl={org.url[locale]} type="ORG" />
            </ViewOrgPageFooterWrapper>
          </ViewOrgPageFooterHolder>
        </PrintHidden>
      )}

      <ButtonBar>
        <ViewOrgPageActionButtons
          org={org}
          isPreview={isPreview}
          mobile
          saved={saved}
          onSave={save}
          onUnsave={unsave}
        />
      </ButtonBar>
    </>
  );
}
